import { useMutation } from '@tanstack/react-query';
import { round } from '@/utils/mathUtils';
import { useCallback } from 'react';

import { GetItemDataApi } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.api';
import type { IItemDataResponse } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';
import type { ApiResponse } from '@/utils/deepmerge';

interface ItemDataMutationParams {
  itemId: number;
  supplierId: number;
}

interface UseItemDataMutationProps {
  setItemData: (data: IItemDataResponse) => void;
  setDiscountInput: (value: string) => void;
  setBnoInput: (value: string) => void;
  setExpireDate: (value: string) => void;
  onError?: (error: unknown) => void;
}

interface ItemDataMutationResult {
  fetchItemData: (
    params: ItemDataMutationParams,
  ) => Promise<ApiResponse<IItemDataResponse>>;
  isPending: boolean;
}

export const useItemDataMutation = ({
  setItemData,
  setDiscountInput,
  setBnoInput,
  setExpireDate,
  onError,
}: UseItemDataMutationProps): ItemDataMutationResult => {
  // Process item data response - memoized to prevent recreation on each render
  const processItemData = useCallback(
    (response: ApiResponse<IItemDataResponse>) => {
      if (!response?.data) return;

      setItemData(response.data);

      const lastIncoming = response.data.itemLastIncoming;
      if (!lastIncoming) return;

      // Update discount with proper validation
      if (
        lastIncoming.Discount !== undefined &&
        lastIncoming.Discount !== null
      ) {
        try {
          const discountValue = round(lastIncoming.Discount, 3);
          setDiscountInput(discountValue.toString());
        } catch (err) {
          console.warn('Error formatting discount value:', err);
        }
      }

      // Update BNO if available
      if (lastIncoming.BNO) {
        setBnoInput(lastIncoming.BNO);
      }

      // Update ExpireDate with validation
      if (lastIncoming.ExpireDate) {
        try {
          const expireDate = new Date(lastIncoming.ExpireDate);
          // Check if date is valid before processing
          if (!isNaN(expireDate.getTime())) {
            const month = (expireDate.getMonth() + 1)
              .toString()
              .padStart(2, '0');
            const year = expireDate.getFullYear();
            setExpireDate(`${month}/${year}`);
          }
        } catch (err) {
          console.warn('Error parsing expireDate:', err);
        }
      }
    },
    [setItemData, setDiscountInput, setBnoInput, setExpireDate],
  );

  const mutation = useMutation({
    mutationFn: async ({ itemId, supplierId }: ItemDataMutationParams) => {
      if (!itemId || !supplierId) {
        throw new Error('itemId and supplierId are required');
      }

      return await GetItemDataApi(itemId, supplierId);
    },
    onSuccess: processItemData,
    onError: (error) => {
      console.error('Error fetching item data:', error);
      // Call provided error handler if available
      if (onError) {
        onError(error);
      }
    },
    // Add retry configuration for better resilience
    retry: 1,
    retryDelay: 1000,
  });

  return {
    fetchItemData: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};
